import { memo } from 'react';

import { AddAlt } from '@mint-lib/icons';

import { Box } from '@myn/mui';

import {
  BUILDER_ROW_TYPES,
  DEFAULT_FIELD_WIDTH,
} from '../../../../../constants/campaignBuilder.js';
import { useSelectedBuyingPlanId } from '../../../../../hooks/useSelectedBuyingPlanId.js';
import { TRACKING_EVENTS } from '../../../../../types/performanceTracking.js';
import { useAddMediaPlanRow } from '../../../../../utils/campaigns/myCampaigns.js';
import { BuilderTableRow } from '../../../types.js';

type Props = {
  row: BuilderTableRow;
  startMeasurePerformance: (event: TRACKING_EVENTS) => void;
  setRowState?: (id: number[], { isNewAdded }: { isNewAdded: boolean }) => void;
};

const AddRowButton = memo(
  ({ row, startMeasurePerformance, setRowState }: Props) => {
    const selectedPlanId = useSelectedBuyingPlanId();

    const { handleAddChannelRequest } = useAddMediaPlanRow();

    const { type, scale, parent } = row.original;
    const isTileRow = type === BUILDER_ROW_TYPES.TILE;
    const componentWidth =
      (row.cells[0]?.column?.width ?? DEFAULT_FIELD_WIDTH) * scale;

    const handleAddRow = async () => {
      const rowOrder =
        type === BUILDER_ROW_TYPES.TILE && row.index
          ? Number(row.index) + 1
          : 0;

      startMeasurePerformance(TRACKING_EVENTS.ROW_ADDITION);

      await handleAddChannelRequest(
        {
          type: BUILDER_ROW_TYPES.TILE,
          parent: isTileRow ? parent : row.id,
          mediaPlan: selectedPlanId,
          order: rowOrder,
        },
        setRowState,
      );
    };

    return (
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          zIndex: 2,
          width: '100%',
          height: 0,
          opacity: 0,
          transition: 'opacity 0.2s',
          '&:hover': {
            opacity: 1,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            transform: 'translateY(-50%)',
            position: 'sticky',
            left: 0,
            width: componentWidth,
            height: 16 * scale,
          }}
        >
          <button
            type="button"
            onClick={handleAddRow}
            className="flex shrink-0 cursor-pointer items-center justify-center rounded-none bg-ui-hoverRow"
            style={{ width: 16 * scale, height: 16 * scale }}
            aria-label="Add row"
            data-testid="add-row-button"
          >
            <AddAlt className="size-full shrink-0 fill-action-focusBorder" />
          </button>
          <Box
            sx={{
              width: '100%',
              height: 2,
              background: (theme) => theme.palette.action.focusBorder,
            }}
          />
        </Box>
      </Box>
    );
  },
);

export default AddRowButton;
